import { PasswordRequirements } from "@assetwatch/auth";
import {
  Alert,
  type AlertProps,
  Button,
  Container,
  Group,
  InlineLink,
  Input,
  Stack,
  Text,
  Title,
} from "@assetwatch/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ChangeEvent, FormEvent } from "react";
import { useState } from "react";
import { z } from "zod";
import {
  useConfirmPasswordResetMutation,
  useSendPasswordResetCodeMutation,
} from "../mutations/useResetPasswordMutations";

const resetPasswordSearchSchema = z.object({
  code: z.string().optional(),
  email: z.string().optional(),
});

type ResetPasswordSearch = z.infer<typeof resetPasswordSearchSchema>;

type PasswordValidation = {
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecialCharacter: boolean;
  hasSufficientLength: boolean;
  matchesConfirm: boolean;
};

type StatusMessage = {
  tone: NonNullable<AlertProps["tone"]>;
  message: string;
};

type CognitoErrorLike = {
  code?: unknown;
  message?: unknown;
  name?: unknown;
  __type?: unknown;
};

const COGNITO_ERROR_MESSAGES: Partial<Record<string, string>> = {
  CodeMismatchException: "The reset code is incorrect.",
  ExpiredCodeException:
    "The reset code has expired. Request a new code and try again.",
  LimitExceededException:
    "Too many reset attempts. Wait a few minutes and try again.",
  TooManyRequestsException:
    "Too many reset attempts. Wait a few minutes and try again.",
  UserNotFoundException: "No account was found for that email address.",
};

export const Route = createFileRoute("/login/reset")({
  validateSearch: (search): ResetPasswordSearch =>
    resetPasswordSearchSchema.parse(search),
  component: ResetPasswordRoute,
});

function validateNewPassword(
  password: string,
  confirmPassword: string,
): PasswordValidation {
  return {
    hasUppercase: password.toLowerCase() !== password,
    hasLowercase: password.toUpperCase() !== password,
    hasNumber: /[0-9]/.test(password),
    hasSpecialCharacter: /[^a-zA-Z0-9]/.test(password),
    hasSufficientLength: password.length >= 10 && password.length <= 32,
    matchesConfirm: password === confirmPassword,
  };
}

function isPasswordValid(validation: PasswordValidation) {
  return (
    validation.hasUppercase &&
    validation.hasLowercase &&
    validation.hasNumber &&
    validation.hasSpecialCharacter &&
    validation.hasSufficientLength &&
    validation.matchesConfirm
  );
}

function isCognitoErrorLike(error: unknown): error is CognitoErrorLike {
  return typeof error === "object" && error !== null;
}

function getStringProperty(
  error: CognitoErrorLike,
  property: keyof CognitoErrorLike,
) {
  const value = error[property];
  return typeof value === "string" ? value : undefined;
}

function getErrorMessage(error: unknown) {
  if (!isCognitoErrorLike(error)) {
    return "Unable to reset your password. Try again.";
  }

  const name =
    getStringProperty(error, "name") ??
    getStringProperty(error, "code") ??
    getStringProperty(error, "__type")?.split("#").pop() ??
    "";
  const message = getStringProperty(error, "message") ?? "";

  if (name === "InvalidPasswordException") {
    return message || "The new password does not meet the requirements.";
  }

  return (
    COGNITO_ERROR_MESSAGES[name] ??
    (message || "Unable to reset your password. Try again.")
  );
}

function ResetPasswordRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [email, setEmail] = useState(search.email ?? "");
  const [code, setCode] = useState(search.code ?? "");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const passwordValidation = validateNewPassword(newPassword, confirmPassword);
  const canSendCode = email.trim().length > 0;
  const canConfirm =
    email.trim().length > 0 &&
    code.trim().length > 0 &&
    isPasswordValid(passwordValidation);

  const sendResetCode = useSendPasswordResetCodeMutation({
    onSuccess: () => {
      setStatus({
        tone: "positive",
        message: "Reset code sent. Check your email.",
      });
    },
    onError: (error) => {
      setStatus({ tone: "negative", message: getErrorMessage(error) });
    },
  });
  const confirmResetPassword = useConfirmPasswordResetMutation({
    onSuccess: () => {
      void navigate({ to: "/", replace: true });
    },
    onError: (error) => {
      setStatus({ tone: "negative", message: getErrorMessage(error) });
    },
  });

  function handleEmailChange(event: ChangeEvent<HTMLInputElement>) {
    setEmail(event.target.value);
  }

  function handleCodeChange(event: ChangeEvent<HTMLInputElement>) {
    setCode(event.target.value);
  }

  function handleNewPasswordChange(event: ChangeEvent<HTMLInputElement>) {
    setNewPassword(event.target.value);
  }

  function handleConfirmPasswordChange(event: ChangeEvent<HTMLInputElement>) {
    setConfirmPassword(event.target.value);
  }

  function handleSendCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSendCode) {
      return;
    }

    sendResetCode.mutate({ username: email.trim() });
  }

  function handleConfirmReset(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canConfirm) {
      return;
    }

    confirmResetPassword.mutate({
      confirmationCode: code.trim(),
      newPassword,
      username: email.trim(),
    });
  }

  return (
    <main className="min-h-screen bg-surface py-(--spacing-32)">
      <Container size="sm">
        <Stack gap={24}>
          <Stack gap={8}>
            <Title size="lg">Reset password</Title>
            <Text tone="subtle">
              Request a reset code, then choose a new password.
            </Text>
          </Stack>

          {status ? <Alert tone={status.tone} title={status.message} /> : null}

          <form onSubmit={handleSendCode}>
            <Stack gap={16}>
              <Input
                autoComplete="email"
                label="Email"
                onChange={handleEmailChange}
                placeholder="Email"
                type="email"
                value={email}
              />
              <Button
                disabled={!canSendCode}
                loading={sendResetCode.isPending}
                loadingText="Sending code"
                type="submit"
              >
                Send reset code
              </Button>
            </Stack>
          </form>

          <form onSubmit={handleConfirmReset}>
            <Stack gap={16}>
              <Input
                autoComplete="one-time-code"
                label="Reset code"
                onChange={handleCodeChange}
                value={code}
              />
              <Text tone="subtext">Your new password must:</Text>
              <PasswordRequirements requirements={passwordValidation} />
              <Input
                autoComplete="new-password"
                label="New password"
                onChange={handleNewPasswordChange}
                type="password"
                value={newPassword}
              />
              <Input
                autoComplete="new-password"
                error={
                  confirmPassword.length > 0 &&
                  !passwordValidation.matchesConfirm
                    ? "Passwords do not match."
                    : undefined
                }
                label="Confirm password"
                onChange={handleConfirmPasswordChange}
                type="password"
                value={confirmPassword}
              />
              <Group justify="between" wrap="wrap">
                <InlineLink href="/">Back to login</InlineLink>
                <Button
                  disabled={!canConfirm}
                  loading={confirmResetPassword.isPending}
                  loadingText="Updating password"
                  type="submit"
                >
                  Update password
                </Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Container>
    </main>
  );
}
